import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { useAuth, ROLES } from '../../contexts/AuthContext';
import { 
  Upload, 
  FileText,
  Users,
  Award,
  Search,
  Building2,
  Shield,
  Star,
  X,
  Plus,
  Loader2,
  Calendar,
  RefreshCw,
  Briefcase,
  Medal,
  Trash2,
  ExternalLink,
  Database,
  Download,
  BookOpen,
  Target,
  Clock,
  Lock,
  Sparkles
} from 'lucide-react';
import KBChatDrawer from '../../components/kb/KBChatDrawer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Asset type configuration
const ASSET_TYPES = {
  PAST_PERFORMANCE: {
    label: 'Past Performance',
    icon: Target,
    color: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    description: 'Winning case studies and contract history'
  },
  KEY_PERSONNEL: {
    label: 'Key Personnel',
    icon: Users,
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    description: 'Team member resumes and biographies'
  },
  CERTIFICATION: {
    label: 'Certifications',
    icon: Shield,
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    description: 'ISO, CMMI, and compliance certifications'
  },
  PROMOTED_PURSUIT: {
    label: 'Promoted Assets',
    icon: Medal,
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    description: 'Gold source content from past bids'
  }
};

const CLEARANCE_OPTIONS = [
  { value: 'NONE', label: 'None' },
  { value: 'CONFIDENTIAL', label: 'Confidential' },
  { value: 'SECRET', label: 'Secret' },
  { value: 'TOP_SECRET', label: 'Top Secret' },
  { value: 'TS_SCI', label: 'TS/SCI' }
];

const CPARS_OPTIONS = [
  { value: 'EXCEPTIONAL', label: 'Exceptional' },
  { value: 'VERY_GOOD', label: 'Very Good' },
  { value: 'SATISFACTORY', label: 'Satisfactory' },
  { value: 'MARGINAL', label: 'Marginal' },
  { value: 'UNSATISFACTORY', label: 'Unsatisfactory' },
  { value: 'NOT_RATED', label: 'Not Rated' }
];

export default function KnowledgeBase() {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [activeTab, setActiveTab] = useState('PAST_PERFORMANCE');
  const [assets, setAssets] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadType, setUploadType] = useState('PAST_PERFORMANCE');
  const [uploading, setUploading] = useState(false);

  // v3.15.0 — Ask the Library drawer
  const [chatOpen, setChatOpen] = useState(false);

  // v3.18.26 — Library ownership flag from the backend. True when the
  // resolved library belongs to a consultancy managing this contractor;
  // false when the user owns their own library (standalone contractor or
  // consultancy member).
  const [isManagedByConsultancy, setIsManagedByConsultancy] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    tags: '',
    file: null,
    // Past Performance
    contract_number: '',
    client_name: '',
    contract_value: '',
    cpars_rating: '',
    // Key Personnel
    person_name: '',
    job_title: '',
    clearance_level: '',
    years_experience: '',
    certifications: '',
    // Certification
    cert_name: '',
    cert_issuer: '',
    cert_number: '',
    expiration_date: ''
  });
  
  const isConsultantOrAdmin = user?.role === ROLES.SUPER_ADMIN || user?.role === ROLES.CONSULTANT || user?.role === ROLES.CONSULTANT_ASSOCIATE;
  // v3.18.25 — Copy variant. A standalone Contractor doesn't have multiple
  // clients, so the "cross-client" framing is misleading. Show contractor-
  // friendly copy if the user belongs to a contractor org AND the backend
  // resolved the library to their own org (no managing consultancy).
  // Consultants & admins continue to see the portfolio framing.
  const isContractorOrg = (
    user?.role === ROLES.CONTRACTOR_ADMIN ||
    user?.role === ROLES.BID_MANAGER ||
    user?.role === ROLES.REVIEWER
  );

  // v3.18.26 — Add-asset gate. Consultants & admins can always upload.
  // CONTRACTOR_ADMIN of a standalone contractor org (library resolves to
  // own org_id) can upload too. Managed-contractor users (library owned by
  // a consultancy) cannot — the consultancy curates. BID_MANAGER /
  // REVIEWER stay read-only by default.
  const canAddAsset = (
    isConsultantOrAdmin ||
    (user?.role === ROLES.CONTRACTOR_ADMIN && !isManagedByConsultancy)
  );

  // Fetch assets
  const fetchAssets = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const params = new URLSearchParams();
      if (activeTab) params.append('asset_type', activeTab);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await fetch(`${API_URL}/api/global-kb/assets?${params}`, { headers });
      
      if (response.ok) {
        const data = await response.json();
        setAssets(data.assets || []);
        setCounts(data.counts || {});
        setIsManagedByConsultancy(Boolean(data.is_managed_by_consultancy));
      } else {
        toast.error('Failed to load knowledge base');
      }
    } catch (err) {
      console.error('Failed to fetch assets:', err);
      toast.error('Failed to load knowledge base');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, activeTab, searchQuery]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  // Reset form
  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      tags: '',
      file: null,
      contract_number: '',
      client_name: '',
      contract_value: '',
      cpars_rating: '',
      person_name: '',
      job_title: '',
      clearance_level: '',
      years_experience: '',
      certifications: '',
      cert_name: '',
      cert_issuer: '',
      cert_number: '',
      expiration_date: ''
    });
  };

  // Handle upload
  const handleUpload = async () => {
    if (!formData.title) {
      toast.error('Title is required');
      return;
    }
    
    setUploading(true);
    try {
      const headers = getAuthHeaders();
      
      if (formData.file) {
        // Upload with file
        const uploadData = new FormData();
        uploadData.append('file', formData.file);
        uploadData.append('asset_type', uploadType);
        uploadData.append('title', formData.title);
        if (formData.description) uploadData.append('description', formData.description);
        if (formData.tags) uploadData.append('tags', formData.tags);
        
        // Type-specific fields
        if (uploadType === 'PAST_PERFORMANCE') {
          if (formData.contract_number) uploadData.append('contract_number', formData.contract_number);
          if (formData.client_name) uploadData.append('client_name', formData.client_name);
          if (formData.contract_value) uploadData.append('contract_value', formData.contract_value);
          if (formData.cpars_rating) uploadData.append('cpars_rating', formData.cpars_rating);
        } else if (uploadType === 'KEY_PERSONNEL') {
          if (formData.person_name) uploadData.append('person_name', formData.person_name);
          if (formData.job_title) uploadData.append('job_title', formData.job_title);
          if (formData.clearance_level) uploadData.append('clearance_level', formData.clearance_level);
          if (formData.years_experience) uploadData.append('years_experience', formData.years_experience);
        } else if (uploadType === 'CERTIFICATION') {
          if (formData.cert_name) uploadData.append('cert_name', formData.cert_name);
          if (formData.cert_issuer) uploadData.append('cert_issuer', formData.cert_issuer);
          if (formData.cert_number) uploadData.append('cert_number', formData.cert_number);
        }
        
        // Remove Content-Type header to let browser set it with boundary
        delete headers['Content-Type'];
        
        const response = await fetch(`${API_URL}/api/global-kb/assets/upload`, {
          method: 'POST',
          headers,
          body: uploadData
        });
        
        if (response.ok) {
          toast.success('Asset uploaded successfully');
          setShowUploadModal(false);
          resetForm();
          fetchAssets();
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Upload failed');
        }
      } else {
        // Create without file
        const response = await fetch(`${API_URL}/api/global-kb/assets`, {
          method: 'POST',
          headers: {
            ...headers,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            asset_type: uploadType,
            title: formData.title,
            description: formData.description || null,
            tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : [],
            contract_number: formData.contract_number || null,
            client_name: formData.client_name || null,
            contract_value: formData.contract_value ? parseFloat(formData.contract_value) : null,
            cpars_rating: formData.cpars_rating || null,
            person_name: formData.person_name || null,
            job_title: formData.job_title || null,
            clearance_level: formData.clearance_level || null,
            years_experience: formData.years_experience ? parseInt(formData.years_experience) : null,
            certifications: formData.certifications ? formData.certifications.split(',').map(c => c.trim()) : [],
            cert_name: formData.cert_name || null,
            cert_issuer: formData.cert_issuer || null,
            cert_number: formData.cert_number || null,
            expiration_date: formData.expiration_date || null
          })
        });
        
        if (response.ok) {
          toast.success('Asset created successfully');
          setShowUploadModal(false);
          resetForm();
          fetchAssets();
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Creation failed');
        }
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // Handle delete
  const handleDelete = async (assetId) => {
    if (!confirm('Are you sure you want to delete this asset?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/global-kb/assets/${assetId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        toast.success('Asset deleted');
        fetchAssets();
      } else {
        toast.error('Failed to delete asset');
      }
    } catch (err) {
      toast.error('Failed to delete asset');
    }
  };

  // Render asset card
  const renderAssetCard = (asset) => {
    const typeConfig = ASSET_TYPES[asset.asset_type] || ASSET_TYPES.PAST_PERFORMANCE;
    const TypeIcon = typeConfig.icon;
    
    return (
      <Card key={asset.id} className="hover:shadow-md transition-shadow">
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${typeConfig.color.split(' ')[0]}`}>
                <TypeIcon className={`w-5 h-5 ${typeConfig.color.split(' ')[1]}`} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-slate-900 truncate">{asset.title}</h3>
                {asset.description && (
                  <p className="text-sm text-slate-500 mt-0.5 line-clamp-2">{asset.description}</p>
                )}
                
                {/* Type-specific details */}
                <div className="flex flex-wrap gap-2 mt-2">
                  {asset.asset_type === 'PAST_PERFORMANCE' && (
                    <>
                      {asset.client_name && (
                        <Badge variant="outline" className="text-xs">
                          <Building2 className="w-3 h-3 mr-1" />
                          {asset.client_name}
                        </Badge>
                      )}
                      {asset.cpars_rating && (
                        <Badge variant="outline" className="text-xs">
                          <Star className="w-3 h-3 mr-1" />
                          {asset.cpars_rating.replace('_', ' ')}
                        </Badge>
                      )}
                    </>
                  )}
                  {asset.asset_type === 'KEY_PERSONNEL' && (
                    <>
                      {asset.person_name && (
                        <Badge variant="outline" className="text-xs">
                          <Users className="w-3 h-3 mr-1" />
                          {asset.person_name}
                        </Badge>
                      )}
                      {asset.job_title && (
                        <Badge variant="outline" className="text-xs">
                          <Briefcase className="w-3 h-3 mr-1" />
                          {asset.job_title}
                        </Badge>
                      )}
                      {asset.clearance_level && asset.clearance_level !== 'NONE' && (
                        <Badge variant="outline" className="text-xs bg-red-50 border-red-200 text-red-700">
                          <Shield className="w-3 h-3 mr-1" />
                          {asset.clearance_level.replace('_', ' ')}
                        </Badge>
                      )}
                    </>
                  )}
                  {asset.asset_type === 'CERTIFICATION' && (
                    <>
                      {asset.cert_issuer && (
                        <Badge variant="outline" className="text-xs">
                          <Award className="w-3 h-3 mr-1" />
                          {asset.cert_issuer}
                        </Badge>
                      )}
                      {asset.expiration_date && (
                        <Badge variant="outline" className="text-xs">
                          <Calendar className="w-3 h-3 mr-1" />
                          Exp: {new Date(asset.expiration_date).toLocaleDateString()}
                        </Badge>
                      )}
                    </>
                  )}
                  {asset.asset_type === 'PROMOTED_PURSUIT' && (
                    <>
                      {asset.source_pursuit_name && (
                        <Badge variant="outline" className="text-xs bg-amber-50 border-amber-200">
                          <Medal className="w-3 h-3 mr-1" />
                          From: {asset.source_pursuit_name}
                        </Badge>
                      )}
                    </>
                  )}
                  {asset.file_name && (
                    <Badge variant="outline" className="text-xs">
                      <FileText className="w-3 h-3 mr-1" />
                      {asset.file_name}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            
            {/* Actions */}
            {isConsultantOrAdmin && (
              <Button
                variant="ghost"
                size="icon"
                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                onClick={() => handleDelete(asset.id)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="knowledge-base-shell">
      {/* Header band — v3.18.24 canonical: px-8 py-4 + max-w-7xl mx-auto */}
      <div className="bg-white border-b border-slate-200 px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <BookOpen className="w-6 h-6 text-teal-700" />
              Reusable Asset Library
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {isContractorOrg ? (
                <>
                  Reusable boilerplate, win themes, past performance, certifications, and key personnel
                  you pull into every pursuit. One library, many pursuits.
                </>
              ) : (
                <>
                  Cross-client templates, winning proposals, and win themes you reuse across pursuits.
                  <span className="text-xs text-slate-400 ml-1">
                    (Per-client past performance, certs &amp; key personnel live on each <strong>Client Profile</strong>.)
                  </span>
                </>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
            <Button
              variant="outline"
              onClick={() => setChatOpen(true)}
              className="text-purple-700 border-purple-300 hover:bg-purple-50"
              data-testid="kb-ask-library-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Ask the Library
            </Button>
            <Button
              variant="outline"
              onClick={fetchAssets}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {canAddAsset && (
              <Button
                onClick={() => {
                  setUploadType(activeTab === 'PROMOTED_PURSUIT' ? 'PAST_PERFORMANCE' : activeTab);
                  setShowUploadModal(true);
                }}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="kb-add-asset-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Asset
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="p-8">
        <div className="max-w-7xl mx-auto space-y-6">

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search knowledge base..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* v3.18.26 — Managed-contractor read-only notice */}
      {isContractorOrg && isManagedByConsultancy && (
        <div
          className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm"
          data-testid="kb-managed-readonly-notice"
        >
          <Lock className="w-4 h-4 text-amber-700 mt-0.5 flex-shrink-0" />
          <div className="text-amber-900">
            <strong>Read-only view.</strong> This library is curated by your consultancy.
            You can browse, search, and pull assets into your pursuits — but new uploads
            must be added by a consultant. Need something added? Ask your consultancy to
            promote it from a Client Profile or upload it directly.
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 p-1">
          {Object.entries(ASSET_TYPES).map(([key, config]) => {
            const Icon = config.icon;
            return (
              <TabsTrigger 
                key={key} 
                value={key}
                className="data-[state=active]:bg-white data-[state=active]:shadow-sm gap-2"
              >
                <Icon className="w-4 h-4" />
                {config.label}
                {counts[key] > 0 && (
                  <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                    {counts[key]}
                  </Badge>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/* Content for each tab */}
        {Object.keys(ASSET_TYPES).map((assetType) => (
          <TabsContent key={assetType} value={assetType} className="mt-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
              </div>
            ) : assets.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <Database className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <h3 className="font-semibold text-slate-700">No {ASSET_TYPES[assetType].label}</h3>
                  <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">
                    {ASSET_TYPES[assetType].description}
                  </p>
                  {canAddAsset && assetType !== 'PROMOTED_PURSUIT' && (
                    <Button
                      className="mt-4"
                      onClick={() => {
                        setUploadType(assetType);
                        setShowUploadModal(true);
                      }}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add {ASSET_TYPES[assetType].label}
                    </Button>
                  )}
                  {assetType === 'PROMOTED_PURSUIT' && (
                    <p className="text-xs text-slate-400 mt-4">
                      Promote documents from pursuit Document Ingestion Hub using the Gold Medal toggle
                    </p>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {assets.map(renderAssetCard)}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-teal-600" />
              Add {ASSET_TYPES[uploadType]?.label || 'Asset'}
            </DialogTitle>
            <DialogDescription>
              Upload a file or create a metadata entry for your knowledge base
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Asset Type Selector */}
            <div>
              <Label>Asset Type</Label>
              <Select value={uploadType} onValueChange={setUploadType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ASSET_TYPES).filter(([key]) => key !== 'PROMOTED_PURSUIT').map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      {config.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Common Fields */}
            <div>
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Enter a descriptive title"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Brief description of this asset"
                rows={2}
              />
            </div>

            {/* File Upload */}
            <div>
              <Label>Attachment (Optional)</Label>
              <Input
                type="file"
                onChange={(e) => setFormData({...formData, file: e.target.files[0]})}
                className="cursor-pointer"
              />
              {formData.file && (
                <p className="text-xs text-slate-500 mt-1">
                  Selected: {formData.file.name}
                </p>
              )}
            </div>

            {/* Type-specific fields */}
            {uploadType === 'PAST_PERFORMANCE' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Client Name</Label>
                    <Input
                      value={formData.client_name}
                      onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                      placeholder="e.g., NASA"
                    />
                  </div>
                  <div>
                    <Label>Contract Number</Label>
                    <Input
                      value={formData.contract_number}
                      onChange={(e) => setFormData({...formData, contract_number: e.target.value})}
                      placeholder="e.g., GS-35F-1234"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Contract Value ($)</Label>
                    <Input
                      type="number"
                      value={formData.contract_value}
                      onChange={(e) => setFormData({...formData, contract_value: e.target.value})}
                      placeholder="e.g., 5000000"
                    />
                  </div>
                  <div>
                    <Label>CPARS Rating</Label>
                    <Select 
                      value={formData.cpars_rating} 
                      onValueChange={(v) => setFormData({...formData, cpars_rating: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select rating" />
                      </SelectTrigger>
                      <SelectContent>
                        {CPARS_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}

            {uploadType === 'KEY_PERSONNEL' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Person Name</Label>
                    <Input
                      value={formData.person_name}
                      onChange={(e) => setFormData({...formData, person_name: e.target.value})}
                      placeholder="Full name"
                    />
                  </div>
                  <div>
                    <Label>Job Title</Label>
                    <Input
                      value={formData.job_title}
                      onChange={(e) => setFormData({...formData, job_title: e.target.value})}
                      placeholder="e.g., Program Manager"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Clearance Level</Label>
                    <Select 
                      value={formData.clearance_level} 
                      onValueChange={(v) => setFormData({...formData, clearance_level: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select clearance" />
                      </SelectTrigger>
                      <SelectContent>
                        {CLEARANCE_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Years Experience</Label>
                    <Input
                      type="number"
                      value={formData.years_experience}
                      onChange={(e) => setFormData({...formData, years_experience: e.target.value})}
                      placeholder="e.g., 15"
                    />
                  </div>
                </div>
              </>
            )}

            {uploadType === 'CERTIFICATION' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Certification Name</Label>
                    <Input
                      value={formData.cert_name}
                      onChange={(e) => setFormData({...formData, cert_name: e.target.value})}
                      placeholder="e.g., ISO 9001:2015"
                    />
                  </div>
                  <div>
                    <Label>Issuing Authority</Label>
                    <Input
                      value={formData.cert_issuer}
                      onChange={(e) => setFormData({...formData, cert_issuer: e.target.value})}
                      placeholder="e.g., BSI Group"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Certificate Number</Label>
                    <Input
                      value={formData.cert_number}
                      onChange={(e) => setFormData({...formData, cert_number: e.target.value})}
                      placeholder="Certificate ID"
                    />
                  </div>
                  <div>
                    <Label>Expiration Date</Label>
                    <Input
                      type="date"
                      value={formData.expiration_date}
                      onChange={(e) => setFormData({...formData, expiration_date: e.target.value})}
                    />
                  </div>
                </div>
              </>
            )}

            <div>
              <Label>Tags (comma-separated)</Label>
              <Input
                value={formData.tags}
                onChange={(e) => setFormData({...formData, tags: e.target.value})}
                placeholder="e.g., IT Services, Cloud, DevOps"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowUploadModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpload}
              disabled={uploading || !formData.title}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  {formData.file ? 'Upload' : 'Create'}
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* v3.15.0 — Ask the Library chatbot drawer */}
      <KBChatDrawer open={chatOpen} onOpenChange={setChatOpen} />
        </div>
      </div>
    </div>
  );
}
